import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { addJob } from '../../lib/supabase/jobs';
import { supabase } from '../../lib/supabase/client';
import Button from '../common/Button';
import AudioRecorder from './AudioRecorder';
import { Upload, X } from 'lucide-react';
import type { Job } from '../../types/jobs';

interface CreateJobFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function CreateJobForm({ onSuccess, onCancel }: CreateJobFormProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    payRate: '',
    isDOE: false,
    description: '',
    type: 'full-time' as Job['type'],
    requirements: [''],
    housing: 'not-provided' as Job['housing'],
    photoUrl: '',
    audioMessageUrl: '',
    contactEmail: user?.email || ''
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('File type must be JPG, PNG, GIF, or WebP');
        return;
      }
      setPhotoFile(file);
      setError(null);
    }
  };

  const handleRemoveFile = () => {
    setPhotoFile(null);
  };

  const handleAudioSave = async (audioBlob: Blob) => {
    if (!user?.id) {
      throw new Error('Please sign in to upload audio message');
    }

    const fileExt = audioBlob.type.split('/')[1].split(';')[0];
    const fileName = `${user.id}/job-${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('job-audio-messages')
      .upload(fileName, audioBlob);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('job-audio-messages')
      .getPublicUrl(fileName);

    setFormData(prev => ({ ...prev, audioMessageUrl: publicUrl }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) {
      setError('Please sign in to post a job');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let photoUrl = formData.photoUrl;

      if (photoFile) {
        setUploading(true);
        const fileExt = photoFile.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;

        const { error: uploadError, data } = await supabase.storage
          .from('job-photos')
          .upload(fileName, photoFile);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('job-photos')
          .getPublicUrl(fileName);

        photoUrl = publicUrl;
        setUploading(false);
      }

      const jobData = {
        ...formData,
        payRate: formData.isDOE ? 'DOE' : Number(formData.payRate),
        farmerId: user.id,
        farmerName: user.user_metadata?.name || user.email || 'Anonymous',
        createdAt: new Date().toISOString(),
        status: 'open' as const,
        requirements: formData.requirements.filter(req => req.trim() !== ''),
        ...(photoUrl && { photoUrl }),
        ...(formData.audioMessageUrl && { audioMessageUrl: formData.audioMessageUrl }),
        ...(formData.contactEmail && { contactEmail: formData.contactEmail })
      };

      const { error: jobError } = await addJob(jobData);
      if (jobError) throw jobError;
      if (onSuccess) onSuccess();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 max-w-xl mx-auto">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Job Title
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:ring-green-500 focus:border-green-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Location
          </label>
          <input
            type="text"
            value={formData.location}
            onChange={e => setFormData(prev => ({ ...prev, location: e.target.value }))}
            className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:ring-green-500 focus:border-green-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Job Type
          </label>
          <select
            value={formData.type}
            onChange={e => setFormData(prev => ({ ...prev, type: e.target.value as Job['type'] }))}
            className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:ring-green-500 focus:border-green-500"
          >
            <option value="full-time">Full Time</option>
            <option value="part-time">Part Time</option>
            <option value="seasonal">Seasonal</option>
            <option value="temporary">Temporary</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Housing
          </label>
          <select
            value={formData.housing}
            onChange={e => setFormData(prev => ({ ...prev, housing: e.target.value as Job['housing'] }))}
            className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:ring-green-500 focus:border-green-500"
          >
            <option value="provided">Housing Provided</option>
            <option value="not-provided">No Housing</option>
            <option value="maybe">Housing May Be Available</option>
          </select>
        </div>
      </div>

      <div>
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            id="isDOE"
            checked={formData.isDOE}
            onChange={e => setFormData(prev => ({ 
              ...prev, 
              isDOE: e.target.checked,
              payRate: e.target.checked ? '' : prev.payRate 
            }))}
            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
          />
          <label htmlFor="isDOE" className="ml-2 text-sm text-gray-600">
            Depends on Experience (DOE)
          </label>
        </div>
        {!formData.isDOE && (
          <div className="flex items-center">
            <span className="text-gray-500 text-sm mr-2">$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.payRate}
              onChange={e => setFormData(prev => ({ ...prev, payRate: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:ring-green-500 focus:border-green-500"
              required={!formData.isDOE}
              placeholder="0.00"
            />
            <span className="text-gray-500 text-sm ml-2">/hour</span>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Contact Email
        </label>
        <input
          type="email"
          value={formData.contactEmail}
          onChange={e => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
          className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:ring-green-500 focus:border-green-500"
          placeholder="your@email.com"
        />
        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-xs text-blue-800">
            <strong>Auto-Submit Feature:</strong> Job seekers with auto-submit enabled will have their applications automatically sent to this email when your job matches their preferences. You'll receive their resume and cover letter instantly.
          </p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={2}
          className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:ring-green-500 focus:border-green-500"
          required
        />
      </div>

      <div>
        <div className="flex justify-between items-center mb-1">
          <label className="block text-sm font-medium text-gray-700">
            Requirements
          </label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setFormData(prev => ({
              ...prev,
              requirements: [...prev.requirements, '']
            }))}
          >
            Add
          </Button>
        </div>
        <div className="space-y-1">
          {formData.requirements.map((req, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={req}
                onChange={e => {
                  const newReqs = [...formData.requirements];
                  newReqs[index] = e.target.value;
                  setFormData(prev => ({ ...prev, requirements: newReqs }));
                }}
                className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm focus:ring-green-500 focus:border-green-500"
                placeholder="Enter requirement"
              />
              {formData.requirements.length > 1 && (
                <button
                  type="button"
                  onClick={() => {
                    const newReqs = formData.requirements.filter((_, i) => i !== index);
                    setFormData(prev => ({ ...prev, requirements: newReqs }));
                  }}
                  className="text-red-500 hover:text-red-700 text-xs px-2"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Photo Upload <span className="text-gray-500">(optional)</span>
        </label>
        {!photoFile ? (
          <div className="relative">
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.gif,.webp"
              onChange={handleFileChange}
              className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:ring-green-500 focus:border-green-500 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
            />
          </div>
        ) : (
          <div className="flex items-center gap-2 p-2 border border-gray-300 rounded-md bg-gray-50">
            <Upload className="h-4 w-4 text-green-600" />
            <span className="text-sm text-gray-700 flex-1 truncate">{photoFile.name}</span>
            <button
              type="button"
              onClick={handleRemoveFile}
              className="text-red-500 hover:text-red-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Upload a photo (max 5MB, JPG, PNG, GIF, or WebP format)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Audio Message <span className="text-gray-500">(optional)</span>
        </label>
        <AudioRecorder
          onSave={handleAudioSave}
          maxDuration={60}
          disabled={loading || uploading}
        />
        {formData.audioMessageUrl && (
          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-700">✓ Audio message uploaded successfully</p>
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-2 pt-3">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          size="sm"
          isLoading={loading || uploading}
        >
          {uploading ? 'Uploading...' : 'Create Job'}
        </Button>
      </div>
    </form>
  );
}